import type { FrontEndTrackingMessage } from '@orus.eu/backend'
import { ensureError } from '@orus.eu/error'

import { isProductionEnvironment } from '../env-utils.ts'
import { logger } from '../logger.ts'

/**
 * Local definition of the Viewed event, as it has been removed from the central tracking specification
 * but is still sent to Meta.
 */
type MetaViewedEvent = {
  event: 'viewed'
  name: string
  url: string
  resourceId: string | null
  title: string
}

export function sendMetaViewedEvent(event: MetaViewedEvent): void {
  sendToMetaEndpoint(event)
}

export function sendTrackingEventToMeta(event: FrontEndTrackingMessage): void {
  // Remove PostHog and routing fields for Meta Server,
  // otherwise our /rest/mkt-events endpoint rejects it as an `invalid input payload`.
  // These fields are injected at runtime by upstream tracking middleware and are not
  // part of the static FrontEndTrackingMessage shape, so we strip them via destructuring.
  const {
    posthog_session_id: _posthogSessionId,
    posthog_distinct_id: _posthogDistinctId,
    posthog_anonymous_id: _posthogAnonymousId,
    canonical_route: _canonicalRoute,
    pathname: _pathname,
    route_pattern: _routePattern,
    ...metaEvent
  } = event as FrontEndTrackingMessage & {
    posthog_session_id?: unknown
    posthog_distinct_id?: unknown
    posthog_anonymous_id?: unknown
    canonical_route?: unknown
    pathname?: unknown
    route_pattern?: unknown
  }

  sendToMetaEndpoint(metaEvent)
}

function sendToMetaEndpoint(event: object): void {
  if (isProductionEnvironment()) {
    const metaEvent = {
      ...event,
      event_id: crypto.randomUUID(),
      page_location: window.location.href,
    }

    fetch('/rest/mkt-events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(metaEvent),
    })
      .then((response) => {
        if (response.status === 400) {
          logger.error(new Error('Meta tracking endpoint rejected event with 400'))
        }
      })
      .catch((err: unknown) => {
        logger.warning('Error while sending tracking event from frontend to Meta server', {
          errStack: ensureError(err).stack ?? null,
        })
      })
  } else {
    logger.info('In production, we would have sent this event to Meta server: ', {
      event: JSON.stringify(event),
    })
  }
}
